import type { Except } from 'type-fest';

export type NotifyStatus = 'error' | 'success';

export type NotifyErrorBody = {
  message?: string;
  extra?: unknown;
  originalResponse?: {
    message?: string;
  };
};

export type NotifyMessage = string | NotifyErrorBody;

export type NotifyOptions = {
  status: NotifyStatus;
  message?: NotifyMessage;
  title: string;
  duration?: number | null;
};

export type NotifyMeta = {
  __NOTIFY__: NotifyOptions;
};

/**
 * The action returned by an action creator wrapped with `notify`: the original
 * action with `meta.__NOTIFY__` guaranteed present.
 */
export type WithNotify<A extends { meta?: object }> = Omit<A, 'meta'> & {
  meta: NonNullable<A['meta']> & NotifyMeta;
};

/**
 * Any action that can come through dispatch. Every reducer and saga `take`
 * receives every action in the system, so global handlers (notifications,
 * middleware, bare `take()`) should accept this and narrow with a type guard
 * rather than claim a slice-specific union.
 */
export type UnknownAction = {
  type: string;
  payload?: unknown;
  meta?: unknown;
  error?: boolean;
};

export const isNotifyAction = (
  action: UnknownAction,
): action is UnknownAction & { meta: NotifyMeta } =>
  typeof action.meta === 'object' &&
  action.meta !== null &&
  '__NOTIFY__' in action.meta;

/**
 * Union of the actions returned by a (possibly nested) map of action creators,
 * so action types are derived from the creators instead of declared twice.
 *
 * @example
 * export type FooAction = ActionsOf<{ foo: typeof foo; bar: typeof bar }>;
 */
export type ActionsOf<T> = {
  [K in keyof T]: T[K] extends (...args: never[]) => infer R
    ? R
    : T[K] extends object
    ? ActionsOf<T[K]>
    : never;
}[keyof T];

/**
 * The member(s) of an action union with the given type(s), e.g. for typing a
 * saga worker or a reducer's cross-slice cases.
 *
 * @example
 * function* watchFoo(action: ActionOfType<FooAction, typeof types.FOO>) {}
 */
export type ActionOfType<
  A extends { type: string },
  T extends A['type'],
> = Extract<A, { type: T }>;

/**
 * @deprecated use ActionOfType and ActionsOf instead.
 */
export type ActionPayload<T> = T extends (...args: any[]) => any
  ? Parameters<T>[0]
  : never;

/**
 * @deprecated derive action types from their creators with `ActionsOf` instead.
 */
export type ActionWithPayload<
  T extends string,
  P = undefined,
  M = unknown,
> = P extends undefined
  ? { type: T; payload: undefined; meta?: M }
  : Except<{ type: T; payload: P; meta?: M }, never>;
